/**
 * Per-seat race HUD snapshots at a replay cursor. Phaser-free so Vitest can
 * cover the same prefix `GameScene.applyReplayRace` scores for one seat.
 */
import type { MapDocument } from './map/types.ts';
import { sharedSplitAmong } from './match/match.ts';
import {
  groupReplayStepsByRound,
  type MatchReplayStep,
} from './match/replayOrder.ts';
import type { SharedSplit } from './match/types.ts';
import type { MovePath } from './path.ts';
import {
  advanceRace,
  boatClockFromCursor,
  calculatedTimeMs,
  createRaceState,
  finishProgress,
  splitsFromMove,
  type RaceSplit,
  type RaceState,
} from './race.ts';
import type { ReplayPlaybackMode } from './replay.ts';

export type ReplaySeatEntry = {
  seatId: number;
  paths: readonly MovePath[];
  racesBefore: readonly RaceState[];
};

export type ReplaySeatCursor = {
  /** -1 before this seat's first turn. */
  localIndex: number;
  progress: number;
};

export type ReplaySeatSnapshot = {
  seatId: number;
  localIndex: number;
  progress: number;
  race: RaceState;
  /** 1-based lap at this cursor. */
  lap: number;
  checkpointsDone: number;
  finished: boolean;
  officialTimeMs: number | null;
  elapsedMs: number;
  round: number;
  /** Boat-clock stamps earned at or before this cursor. */
  splits: readonly RaceSplit[];
};

const splitsByTurnCache = new WeakMap<
  readonly MovePath[],
  { map: MapDocument; byTurn: readonly RaceSplit[][] }
>();

export function replaySeatCursorAt(
  mode: ReplayPlaybackMode,
  steps: readonly MatchReplayStep[],
  stepIndex: number,
  moveProgress: number,
  seatId: number,
  pathCount: number,
  rounds: readonly MatchReplayStep[][] = groupReplayStepsByRound(steps),
): ReplaySeatCursor {
  if (mode === 'simultaneous') {
    return simultaneousSeatCursor(rounds, stepIndex, moveProgress, seatId, pathCount);
  }

  return turnByTurnSeatCursor(steps, stepIndex, moveProgress, seatId, pathCount);
}

function turnByTurnSeatCursor(
  steps: readonly MatchReplayStep[],
  stepIndex: number,
  moveProgress: number,
  seatId: number,
  pathCount: number,
): ReplaySeatCursor {
  if (steps.length === 0 || pathCount <= 0) {
    return { localIndex: -1, progress: 0 };
  }

  if (stepIndex >= steps.length) {
    return { localIndex: pathCount - 1, progress: 1 };
  }

  const current = steps[stepIndex];

  if (current?.seatId === seatId) {
    return { localIndex: current.localIndex, progress: moveProgress };
  }

  let last = -1;

  for (let index = 0; index < stepIndex; index += 1) {
    const step = steps[index];

    if (step?.seatId === seatId) {
      last = step.localIndex;
    }
  }

  if (last < 0) {
    return { localIndex: -1, progress: 0 };
  }

  return { localIndex: last, progress: 1 };
}

function simultaneousSeatCursor(
  rounds: readonly MatchReplayStep[][],
  stepIndex: number,
  moveProgress: number,
  seatId: number,
  pathCount: number,
): ReplaySeatCursor {
  if (rounds.length === 0 || pathCount <= 0) {
    return { localIndex: -1, progress: 0 };
  }

  if (stepIndex >= rounds.length) {
    return { localIndex: pathCount - 1, progress: 1 };
  }

  const racing = (rounds[stepIndex] ?? []).some((step) => step.seatId === seatId);

  if (racing) {
    return { localIndex: stepIndex, progress: moveProgress };
  }

  return { localIndex: pathCount - 1, progress: 1 };
}

export function replaySeatSnapshot(
  map: MapDocument,
  entry: ReplaySeatEntry,
  cursor: ReplaySeatCursor,
): ReplaySeatSnapshot {
  const race = seatRaceAt(map, entry, cursor);
  const officialTimeMs = seatOfficialTimeMs(map, entry, cursor, race);
  const finished = officialTimeMs !== null;

  return {
    seatId: entry.seatId,
    localIndex: cursor.localIndex,
    progress: cursor.progress,
    race,
    lap: race.lap,
    checkpointsDone: race.passedCheckpointIds.size,
    finished,
    officialTimeMs,
    elapsedMs: seatElapsedMs(cursor, officialTimeMs),
    round: seatRoundAt(cursor, entry.paths.length, finished),
    splits: splitsAtReplayCursor(map, entry, cursor),
  };
}

/**
 * Latest checkpoint (or lap / finish) every seat has reached at these snapshots.
 * Same gap live play shows once the field shares a milestone.
 */
export function replaySharedSplit(snapshots: readonly ReplaySeatSnapshot[]): SharedSplit | null {
  return sharedSplitAmong(
    snapshots.map((snapshot) => ({ id: snapshot.seatId, splits: snapshot.splits })),
  );
}

/**
 * Splits earned on completed turns, plus any on the current turn whose crossing
 * progress the cursor has already reached. Full-turn stamps are cached per path list.
 */
export function splitsAtReplayCursor(
  map: MapDocument,
  entry: ReplaySeatEntry,
  cursor: ReplaySeatCursor,
): RaceSplit[] {
  if (cursor.localIndex < 0 || entry.paths.length === 0) {
    return [];
  }

  const byTurn = splitsByTurn(map, entry);

  if (cursor.localIndex >= byTurn.length) {
    return byTurn.flat();
  }

  const earned: RaceSplit[] = [];

  for (let index = 0; index < cursor.localIndex; index += 1) {
    earned.push(...(byTurn[index] ?? []));
  }

  for (const split of byTurn[cursor.localIndex] ?? []) {
    if (split.progress <= cursor.progress + 1e-9) {
      earned.push(split);
    }
  }

  return earned;
}

function splitsByTurn(map: MapDocument, entry: ReplaySeatEntry): readonly RaceSplit[][] {
  const cached = splitsByTurnCache.get(entry.paths);

  if (cached?.map === map) {
    return cached.byTurn;
  }

  const byTurn = entry.paths.map((path, index) =>
    splitsFromMove(map, entry.racesBefore[index] ?? createRaceState(), path, index + 1),
  );
  splitsByTurnCache.set(entry.paths, { map, byTurn });
  return byTurn;
}

export function replaySeatSnapshots(
  map: MapDocument,
  seats: readonly ReplaySeatEntry[],
  mode: ReplayPlaybackMode,
  steps: readonly MatchReplayStep[],
  stepIndex: number,
  moveProgress: number,
  progressBySeat?: ReadonlyMap<number, number>,
): ReplaySeatSnapshot[] {
  const rounds = groupReplayStepsByRound(steps);

  return seats.map((entry) => {
    const cursor = replaySeatCursorAt(
      mode,
      steps,
      stepIndex,
      moveProgress,
      entry.seatId,
      entry.paths.length,
      rounds,
    );
    const override = progressBySeat?.get(entry.seatId);

    if (override !== undefined && cursor.localIndex >= 0) {
      return replaySeatSnapshot(map, entry, { ...cursor, progress: override });
    }

    return replaySeatSnapshot(map, entry, cursor);
  });
}

function seatRaceAt(
  map: MapDocument,
  entry: ReplaySeatEntry,
  cursor: ReplaySeatCursor,
): RaceState {
  if (cursor.localIndex < 0) {
    return entry.racesBefore[0] ?? createRaceState();
  }

  const before = entry.racesBefore[cursor.localIndex] ?? createRaceState();
  return advanceRace(map, before, entry.paths[cursor.localIndex] ?? null, cursor.progress);
}

function seatOfficialTimeMs(
  map: MapDocument,
  entry: ReplaySeatEntry,
  cursor: ReplaySeatCursor,
  race: RaceState,
): number | null {
  if (cursor.localIndex < 0) {
    return null;
  }

  for (let index = 0; index < cursor.localIndex; index += 1) {
    const after = entry.racesBefore[index + 1];

    if (!after?.complete) {
      continue;
    }

    const crossed = finishProgress(
      map,
      entry.racesBefore[index] ?? createRaceState(),
      entry.paths[index] ?? null,
    );
    return crossed !== null
      ? calculatedTimeMs(index + 1, crossed)
      : calculatedTimeMs(index + 1, 1);
  }

  if (!race.complete) {
    return null;
  }

  const crossed = finishProgress(
    map,
    entry.racesBefore[cursor.localIndex] ?? createRaceState(),
    entry.paths[cursor.localIndex] ?? null,
  );

  if (crossed === null) {
    return calculatedTimeMs(cursor.localIndex + 1, cursor.progress);
  }

  if (crossed > cursor.progress + 1e-9) {
    return null;
  }

  return calculatedTimeMs(cursor.localIndex + 1, crossed);
}

function seatElapsedMs(
  cursor: ReplaySeatCursor,
  officialTimeMs: number | null,
): number {
  if (cursor.localIndex < 0) {
    return 0;
  }

  const raw = boatClockFromCursor(
    cursor.localIndex + cursor.progress,
    cursor.localIndex + 1,
    1,
  );
  return officialTimeMs !== null ? Math.min(raw, officialTimeMs) : raw;
}

function seatRoundAt(
  cursor: ReplaySeatCursor,
  pathCount: number,
  finished: boolean,
): number {
  if (pathCount <= 0 || cursor.localIndex < 0) {
    return 1;
  }

  if (finished) {
    return cursor.localIndex + 1;
  }

  if (cursor.progress >= 1) {
    return Math.min(cursor.localIndex + 2, pathCount);
  }

  return cursor.localIndex + 1;
}
